<script setup>
import {
  CDropdown, CDropdownDivider,
  CDropdownHeader, CDropdownItem,
  CDropdownMenu,
  CDropdownToggle
} from "@coreui/vue/dist/esm/components/dropdown/index.js";
import {CAvatar} from "@coreui/vue/dist/esm/components/avatar/index.js";
import CIcon from "@coreui/icons-vue";
import { cilUser, cilSettings, cilLockLocked } from '@coreui/icons';
import { useStore } from '~/stores';
import { useNtaskApi } from '~/composables';

const store = useStore();
const { usuario } = storeToRefs(store);
const ntaskApi = useNtaskApi();

const avatarUrl = ref('');
const avatarLoading = ref(false);
const avatarBlobUrls = ref({});

const obterFotoUsuario = () => {
  try {
    const authData = localStorage.getItem('auth_data');
    if (authData) {
      const parsed = JSON.parse(authData);
      if (parsed.usuario && parsed.usuario.foto) {
        return parsed.usuario.foto;
      }
    }
  } catch (error) {
    console.error('Erro ao ler auth_data:', error);
  }
  
  if (usuario.value?.foto) {
    return usuario.value.foto;
  }
  
  return null;
};

const carregarAvatarAsync = async (foto) => {
  if (avatarLoading.value || !foto) {
    return;
  }

  avatarLoading.value = true;
  
  try {
    const cacheKey = `avatar_${foto}`;
    
    if (avatarBlobUrls.value[cacheKey]) {
      avatarUrl.value = avatarBlobUrls.value[cacheKey];
      avatarLoading.value = false;
      return;
    }

    const cachedAvatar = localStorage.getItem(cacheKey);
    if (cachedAvatar) {
      avatarUrl.value = cachedAvatar;
      avatarBlobUrls.value[cacheKey] = cachedAvatar;
      avatarLoading.value = false;
      return;
    }

    const response = await ntaskApi.arquivos.getArquivo(foto);
    
    if (typeof response === 'string' && response.startsWith('http')) {
      avatarUrl.value = response;
      localStorage.setItem(cacheKey, response);
      avatarBlobUrls.value[cacheKey] = response;
      avatarLoading.value = false;
      return;
    }

    if (response instanceof Blob) {
      const blobUrl = URL.createObjectURL(response);
      avatarBlobUrls.value[cacheKey] = blobUrl;
      avatarUrl.value = blobUrl;
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          localStorage.setItem(cacheKey, reader.result);
        }
      };
      reader.readAsDataURL(response);
    } else {
      avatarUrl.value = '/img/icons/modulos/admin.svg';
    }
  } catch (error) {
    console.error('Erro ao carregar avatar:', error);
    avatarUrl.value = '/img/icons/modulos/admin.svg';
  } finally {
    avatarLoading.value = false;
  }
};

const avatar = computed(() => {
  if (avatarUrl.value) {
    return avatarUrl.value;
  }
  
  const foto = obterFotoUsuario();
  if (foto) {
    nextTick(() => {
      carregarAvatarAsync(foto);
    });
  }
  
  return '/img/icons/modulos/admin.svg';
});

watch(() => usuario.value?.foto, (novaFoto) => {
  if (novaFoto) {
    carregarAvatarAsync(novaFoto);
  }
}, { immediate: true });

watch(() => {
  try {
    const authData = localStorage.getItem('auth_data');
    return authData ? JSON.parse(authData)?.usuario?.foto : null;
  } catch {
    return null;
  }
}, (novaFoto, oldFoto) => {
  if (novaFoto && novaFoto !== oldFoto) {
    carregarAvatarAsync(novaFoto);
  }
}, { immediate: true });

onMounted(() => {
  const foto = obterFotoUsuario();
  if (foto) {
    carregarAvatarAsync(foto);
  }
});

const sair = () => {
  console.log('Saindo...');
};

const irParaConfiguracoes = () => {
  navigateTo('/configuracoes');
};
</script>

<template>

  <CDropdown placement="bottom-end" variant="nav-item">
    <CDropdownToggle :caret="false">
      <CAvatar v-if="avatarUrl" :src="avatar" size="md" />
      <CIcon v-else :icon="cilUser" size="lg" class="text-white" />
    </CDropdownToggle>
    <CDropdownMenu class="pt-0">
      <CDropdownHeader
          component="h6"
          class="bg-body-secondary text-body-secondary fw-semibold mb-2 rounded-top"
      >
        Minha Conta
      </CDropdownHeader>
      <CDropdownItem role="button" @click="irParaConfiguracoes">
        <CIcon :icon="cilSettings" class="me-2" />Configurações
      </CDropdownItem>
      <CDropdownDivider />
      <CDropdownItem role="button" @click="sair">
        <CIcon :icon="cilLockLocked" /> Sair
      </CDropdownItem>
    </CDropdownMenu>
  </CDropdown>

</template>